<script setup lang="ts">
import { computed } from 'vue';
import { formatCurrency } from '@/utils/currency';
import { type SalaryPayment, type SalaryBreakdown } from '@/api/payments';

interface Props {
  salaryPayment: SalaryPayment;
}

const props = defineProps<Props>();

interface SalaryDetailRow {
  concept: string;
  value: string;
  isAmount?: boolean;
}

const salaryBreakdown = computed(() => props.salaryPayment.salaryBreakdown ?? []);
const salaryCost = computed(() => props.salaryPayment.cost ?? 0);
const salaryShiftsCount = computed(() => props.salaryPayment.shiftsCount ?? 0);

const salaryEffectiveDays = computed(() => (
  props.salaryPayment.totalEffectiveDays ??
  salaryBreakdown.value.reduce((sum, b) => sum + b.effectiveDays, 0)
));

const salaryShiftsWithVariable = computed(() => (
  props.salaryPayment.totalShiftsWithVariable ??
  salaryBreakdown.value.reduce((sum, b) => sum + (b.shiftsWithVariable ?? 0), 0)
));

const salaryConfig = computed(() => {
  const breakdown = salaryBreakdown.value[0];
  if (!breakdown) return null;
  return {
    baseSalary: breakdown.baseSalary ?? 0,
    totalMonthDays: breakdown.totalMonthDays ?? 0,
    minimumShiftsThreshold: breakdown.minimumShiftsThreshold ?? 0,
    amountPerShift: breakdown.amountPerShift ?? 0,
  };
});

const totalBaseSalary = computed(() => (
  salaryBreakdown.value.reduce((sum, b) => sum + (b.baseSalary ?? 0), 0)
));

const totalMonthDays = computed(() => (
  salaryBreakdown.value.reduce((sum, b) => sum + (b.totalMonthDays ?? 0), 0)
));

const hasMultipleSalaryPeriods = computed(() => salaryBreakdown.value.length > 1);

function num(value: number | undefined | null): number {
  return value ?? 0;
}

function formatVariableSalaryValue(
  amountPerShift: number,
  shiftsWithVariable: number,
  variableSalary: number,
): string {
  if (amountPerShift > 0 && shiftsWithVariable > 0) {
    const formula = `${shiftsWithVariable} turnos × ${formatCurrency(amountPerShift)}`;
    return `${formula} = ${formatCurrency(variableSalary)}`;
  }
  return formatCurrency(variableSalary);
}

function buildThresholdRows(threshold: number, shiftsWithVariable: number): SalaryDetailRow[] {
  if (threshold <= 0) return [];
  return [
    { concept: 'Umbral mínimo para variable', value: `${threshold} turnos` },
    { concept: 'Turnos sobre el umbral', value: `${shiftsWithVariable} turnos` },
  ];
}

function buildVariableRows(
  config: { minimumShiftsThreshold: number; amountPerShift: number } | null,
  totalShifts: number,
  shiftsWithVariable: number,
  variableSalary: number,
): SalaryDetailRow[] {
  const threshold = config?.minimumShiftsThreshold ?? 0;
  const amountPerShift = config?.amountPerShift ?? 0;

  const rows: SalaryDetailRow[] = [
    { concept: 'Turnos realizados', value: `${totalShifts} turnos` },
    ...buildThresholdRows(threshold, shiftsWithVariable),
  ];

  if (amountPerShift > 0) {
    rows.push({
      concept: 'Monto por turno variable',
      value: formatCurrency(amountPerShift),
      isAmount: true,
    });
  }

  const variableValue = formatVariableSalaryValue(amountPerShift, shiftsWithVariable, variableSalary);
  rows.push({ concept: 'Sueldo Variable', value: variableValue, isAmount: true });

  return rows;
}

function buildBaseRows(
  fullSalary: number,
  monthDays: number,
  effectiveDays: number,
  proratedSalary: number,
): SalaryDetailRow[] {
  return [
    { concept: 'Sueldo mensual completo', value: formatCurrency(fullSalary), isAmount: true },
    { concept: 'Días del período', value: `${monthDays} días` },
    { concept: 'Días considerados', value: `${effectiveDays} días` },
    {
      concept: 'Sueldo Base Líquido (prorrateado)',
      value: `${formatCurrency(fullSalary)} × ${effectiveDays}/${monthDays} = ${formatCurrency(proratedSalary)}`,
      isAmount: true,
    },
  ];
}

function buildBreakdownDetailRows(b: SalaryBreakdown): SalaryDetailRow[] {
  const config = {
    minimumShiftsThreshold: num(b.minimumShiftsThreshold),
    amountPerShift: num(b.amountPerShift),
  };
  const base = [
    num(b.baseSalary),
    num(b.totalMonthDays),
    num(b.effectiveDays),
    num(b.proratedBaseSalary),
  ] as const;
  const variable = [
    num(b.shiftsInPeriod),
    num(b.shiftsWithVariable),
    num(b.variableSalary),
  ] as const;
  return [...buildBaseRows(...base), ...buildVariableRows(config, ...variable)];
}

const salaryDetailRows = computed((): SalaryDetailRow[] => [
  ...buildBaseRows(
    totalBaseSalary.value,
    totalMonthDays.value,
    salaryEffectiveDays.value,
    props.salaryPayment.baseSalary ?? 0,
  ),
  ...buildVariableRows(
    salaryConfig.value,
    salaryShiftsCount.value,
    salaryShiftsWithVariable.value,
    props.salaryPayment.variableSalary ?? 0,
  ),
]);

function localizedDate(date: string) {
  return date.split('-').reverse().join('/');
}
</script>

<template>
  <v-expansion-panel>
    <v-expansion-panel-title class="font-weight-bold">
      Sueldo
      <v-spacer />
      <v-chip
        color="green-darken-3"
        class="mr-8"
      >
        {{ formatCurrency(salaryCost) }}
      </v-chip>
    </v-expansion-panel-title>
    <v-expansion-panel-text>
      <!-- Single Salary Period View -->
      <template v-if="!hasMultipleSalaryPeriods">
        <v-table
          density="comfortable"
          class="elevation-1"
        >
          <thead>
            <tr>
              <th class="font-weight-bold">
                Concepto
              </th>
              <th class="font-weight-bold text-end">
                Valor
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in salaryDetailRows"
              :key="index"
              :class="{ 'bg-grey-lighten-4': row.isAmount }"
            >
              <td>{{ row.concept }}</td>
              <td class="text-end">
                <span :class="{ 'font-weight-bold': row.isAmount }">
                  {{ row.value }}
                </span>
              </td>
            </tr>
            <tr class="font-weight-bold">
              <td>Total a Pagar</td>
              <td class="text-end">
                <v-chip color="green-darken-3">
                  {{ formatCurrency(salaryCost) }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-table>
      </template>

      <!-- Multiple Salary Periods View -->
      <template v-else>
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          Este período incluye {{ salaryBreakdown.length }} sueldos diferentes.
          A continuación se muestra el detalle de cada uno.
        </v-alert>

        <div
          v-for="(breakdown, idx) in salaryBreakdown"
          :key="breakdown.salaryId"
          class="mb-6"
        >
          <h4 class="mb-2 d-flex align-center">
            <v-icon
              icon="mdi-calendar-range"
              size="small"
              class="mr-2"
            />
            Período {{ idx + 1 }}: {{ localizedDate(breakdown.effectiveStart) }} -
            {{ localizedDate(breakdown.effectiveEnd) }}
            <v-spacer />
            <v-chip
              color="primary"
              size="small"
            >
              {{ formatCurrency(breakdown.total) }}
            </v-chip>
          </h4>
          <v-table
            density="comfortable"
            class="elevation-1"
          >
            <thead>
              <tr>
                <th class="font-weight-bold">
                  Concepto
                </th>
                <th class="font-weight-bold text-end">
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, rowIdx) in buildBreakdownDetailRows(breakdown)"
                :key="rowIdx"
                :class="{ 'bg-grey-lighten-4': row.isAmount }"
              >
                <td>{{ row.concept }}</td>
                <td class="text-end">
                  <span :class="{ 'font-weight-bold': row.isAmount }">
                    {{ row.value }}
                  </span>
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>Subtotal Período</td>
                <td class="text-end">
                  <v-chip
                    color="primary"
                    size="small"
                  >
                    {{ formatCurrency(breakdown.total) }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>

        <!-- Grand Total -->
        <v-table
          density="comfortable"
          class="elevation-1"
        >
          <tbody>
            <tr class="font-weight-bold">
              <td>Total a Pagar (suma de todos los períodos)</td>
              <td class="text-end">
                <v-chip color="green-darken-3">
                  {{ formatCurrency(salaryCost) }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-table>
      </template>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>
